import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs'
import {
  ControllingSortFieldSchema,
  ScheduleStatusSchema,
  DeviationStatusSchema,
  ControllingFollowUpReasonCodeSchema,
  TimeEntryCategorySchema,
} from '@myapp/contract'
import {
  INITIAL_CONTROLLING_FILTERS,
  ControllingFiltersForm,
  ControllingSortField,
  ControllingSortDirection,
} from './controllingFilters.types'

const SORT_FIELDS = new Set<ControllingSortField>(
  ControllingSortFieldSchema.options,
)
const SORT_DIRECTIONS = new Set<ControllingSortDirection>(['ASC', 'DESC'])

const REASON_FILTER_VALUES = [
  ...ControllingFollowUpReasonCodeSchema.options,
  ...TimeEntryCategorySchema.options,
] as const

export const useControllingFiltersParams = () =>
  useQueryStates(
    {
      dateFrom: parseAsString.withDefault(INITIAL_CONTROLLING_FILTERS.dateFrom),
      dateTo: parseAsString.withDefault(INITIAL_CONTROLLING_FILTERS.dateTo),
      workOrderId: parseAsString.withDefault(
        INITIAL_CONTROLLING_FILTERS.workOrderId,
      ),
      technicianId: parseAsString.withDefault(
        INITIAL_CONTROLLING_FILTERS.technicianId,
      ),
      tenant: parseAsString.withDefault(INITIAL_CONTROLLING_FILTERS.tenant),
      ve: parseAsString.withDefault(INITIAL_CONTROLLING_FILTERS.ve),
      building: parseAsString.withDefault(INITIAL_CONTROLLING_FILTERS.building),
      tradeId: parseAsString.withDefault(INITIAL_CONTROLLING_FILTERS.tradeId),
      technicianTradeId: parseAsString.withDefault(
        INITIAL_CONTROLLING_FILTERS.technicianTradeId,
      ),
      scheduleStatus: parseAsArrayOf(
        parseAsStringLiteral(ScheduleStatusSchema.options),
      ).withDefault(INITIAL_CONTROLLING_FILTERS.scheduleStatus),
      deviationStatus: parseAsArrayOf(
        parseAsStringLiteral(DeviationStatusSchema.options),
      ).withDefault(INITIAL_CONTROLLING_FILTERS.deviationStatus),
      reasons: parseAsArrayOf(
        parseAsStringLiteral(REASON_FILTER_VALUES),
      ).withDefault(INITIAL_CONTROLLING_FILTERS.reasons),
      actualTravelHoursMin: parseAsString.withDefault(
        INITIAL_CONTROLLING_FILTERS.actualTravelHoursMin,
      ),
      actualTravelHoursMax: parseAsString.withDefault(
        INITIAL_CONTROLLING_FILTERS.actualTravelHoursMax,
      ),
      actualWorkHoursMin: parseAsString.withDefault(
        INITIAL_CONTROLLING_FILTERS.actualWorkHoursMin,
      ),
      actualWorkHoursMax: parseAsString.withDefault(
        INITIAL_CONTROLLING_FILTERS.actualWorkHoursMax,
      ),
      actualTotalHoursMin: parseAsString.withDefault(
        INITIAL_CONTROLLING_FILTERS.actualTotalHoursMin,
      ),
      actualTotalHoursMax: parseAsString.withDefault(
        INITIAL_CONTROLLING_FILTERS.actualTotalHoursMax,
      ),
      deviationMin: parseAsString.withDefault(
        INITIAL_CONTROLLING_FILTERS.deviationMin,
      ),
      deviationMax: parseAsString.withDefault(
        INITIAL_CONTROLLING_FILTERS.deviationMax,
      ),
      sortField: parseAsString.withDefault(INITIAL_CONTROLLING_FILTERS.sortField),
      sortDirection: parseAsString.withDefault(
        INITIAL_CONTROLLING_FILTERS.sortDirection,
      ),
      page: parseAsInteger.withDefault(1),
    },
    { clearOnDefault: true },
  )

export type ControllingFiltersParams = ReturnType<
  typeof useControllingFiltersParams
>[0]

export const toControllingFiltersForm = (
  params: ControllingFiltersParams,
): ControllingFiltersForm => ({
  dateFrom: params.dateFrom,
  dateTo: params.dateTo,
  workOrderId: params.workOrderId,
  technicianId: params.technicianId,
  tenant: params.tenant,
  ve: params.ve,
  building: params.building,
  tradeId: params.tradeId,
  technicianTradeId: params.technicianTradeId,
  scheduleStatus: params.scheduleStatus,
  deviationStatus: params.deviationStatus,
  reasons: params.reasons,
  actualTravelHoursMin: params.actualTravelHoursMin,
  actualTravelHoursMax: params.actualTravelHoursMax,
  actualWorkHoursMin: params.actualWorkHoursMin,
  actualWorkHoursMax: params.actualWorkHoursMax,
  actualTotalHoursMin: params.actualTotalHoursMin,
  actualTotalHoursMax: params.actualTotalHoursMax,
  deviationMin: params.deviationMin,
  deviationMax: params.deviationMax,
  sortField: SORT_FIELDS.has(params.sortField as ControllingSortField)
    ? (params.sortField as ControllingSortField)
    : INITIAL_CONTROLLING_FILTERS.sortField,
  sortDirection: SORT_DIRECTIONS.has(
    params.sortDirection as ControllingSortDirection,
  )
    ? (params.sortDirection as ControllingSortDirection)
    : INITIAL_CONTROLLING_FILTERS.sortDirection,
})
